<template>
  <div class="AppMenuDesktop">
    <AppMenuTitleBox></AppMenuTitleBox>
    <ul class="menuItems">
      <RouterLink to="/" custom v-slot="{ navigate }">
        <li v-ripple class="p-ripple" @click="navigate()">
          Status
        </li>
      </RouterLink>
      <li v-ripple @click="toggleSettingsMenu($event)" class="p-ripple">
        Settings
        <TieredMenu ref="settingsMenu" :model="settingsMenuItems" :popup="true"></TieredMenu>
      </li>
      <RouterLink to="/records" custom v-slot="{ navigate }">
        <li v-ripple class="p-ripple" @click="navigate()">
          Records
        </li>
      </RouterLink>
      <li v-if="store.showAdvanced" v-ripple class="technicianSettings p-ripple" @click="toggleTechnicianMenu($event)">
        Technician
        <TieredMenu ref="technicianMenu" :model="technicianMenuItems" :popup="true"></TieredMenu>
      </li>
    </ul>
  </div>
</template>

<script setup>
import store from "./store.js";
import router from "./router.js";
import AppMenuTitleBox from "./AppMenuTitleBox.vue";
import { computed, ref } from "vue";
import { promiseTimeout } from "@vueuse/core";
import { getBoardHostname, handleAdvancedToggleClick } from "./appUtility.js";

function toggleAdvanced() {
  store.showAdvanced = !store.showAdvanced;
}

function toggleSettingsMenu(event) {
  settingsMenu.value.toggle(event);
}

let boardsOnSite = computed(() => {
  return store.sessions[0].system.boardsOnSite.children;
});

function isThisBoard(board) {
  let system = store.sessions[0].system;
  return board.macAddress.value == system.macAddr.value;
}

let boardsMenu = ref();
let boardsMenuItems = computed(() => {
  return boardsOnSite.value.map(board => ({
    board,
    command: () => {
      if (board.online.value)
        location = `http://${getBoardHostname(board)}`;
    }
  }));
});

function onClickTitleArea(event) {
  boardsMenu.value.toggle(event);
  handleAdvancedToggleClick();
}

let showingBoardsOnSiteDialog = ref(false);

function showBoardsOnSiteDialog() {
  boardsMenu.value.hide();
  showingBoardsOnSiteDialog.value = !showingBoardsOnSiteDialog.value;
}

let settingsMenu = ref();
let settingsMenuItems = computed(() => {
  let items = store.sessions[0].system.settingsMenu.value
  return [
    ... items.map(item => ({
      label: item.label,
      command: () => router.push(item.url)
    })),
    { 
      separator: true 
    },
    {
      label: "Save / Load settings",
      command: () => router.push("/saveAndLoadSettings")
    }
  ];
});

function toggleTechnicianMenu(event) {
  technicianMenu.value.toggle(event);
}

let technicianMenu = ref();
let technicianMenuItems = computed(() => {
  let isWebview = Boolean(window.chrome?.webview);
  let items = store.sessions[0].system.technicianMenu.value;
  return [
    ... items.map(item => ({
      label: item.label,
      command: () => router.push(item.url)
    })),
    {
      label: "Reload page",
      command: () => window.location.reload()
    },
    {
      label: "Webview ...",
      visible: isWebview,
      items: [
        {
          label: "Zoom in",
          command: () => {
            window.chrome?.webview.postMessage("zoomIn");
            reopenWebviewSubmenu();
          }
        },
        {
          label: "Zoom out",
          keepOpen: true,
          command: () => {
            window.chrome?.webview.postMessage("zoomOut");
            reopenWebviewSubmenu();
          }
        },
        {
          label: "Reset zoom",
          command: () => window.chrome?.webview.postMessage("resetZoom")
        },
        {
          label: "Show webview settings",
          command: () => window.chrome?.webview.postMessage("showWebviewMenu")
        }
      ]
    },
    {
      label: "Shutdown options ...",
      items: [
        {
          label: "End process",
          command: () => confirmShutdown("Really end process?", endProcess)
        },
        {
          label: "Reboot",
          command: () => confirmShutdown("Really reboot?", reboot)
        },
        {
          label: "Power off",
          command: () => confirmShutdown("Really POWER OFF?", powerOff)
        }
      ]
    }
  ];
});

async function reopenWebviewSubmenu() {
  await promiseTimeout(150);
  technicianMenu.value.toggle({});
  await promiseTimeout(150);
  let elems = [ ... document.querySelectorAll(".p-tieredmenu span")];
  let elem = elems.find(elem => elem.innerText == "Webview ...");
  elem.click();
}

function confirmShutdown(prompt, callback) {
  if (confirm(prompt))
    callback();
}

function endProcess() {
  let session = store.sessions[0];
  session.queueRequest("/cmd/endProcess");
};

function reboot() {
  let session = store.sessions[0];
  session.queueRequest("/cmd/reboot-step1");
  session.queueRequest("/cmd/reboot-step2");
};

function powerOff() {
  let session = store.sessions[0];
  session.queueRequest("/cmd/powerOff-step1");
  session.queueRequest("/cmd/powerOff-step2");
}
</script>

<style scoped>
.AppMenuDesktop {
  height: 36px;
  flex-shrink: 0;
  display: flex;
  align-items: stretch;
  font-size: large;
  background: #0063B1; 
  color: white;
  font-weight: 500;
}
.AppMenuDesktop .titleArea {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  padding-left: 45px;
  padding-right: 45px;
  min-width: 225px;
}
.AppMenuDesktop .networkBlinker {
  display: flex;
  align-items: center;
}
.AppMenuDesktop .siteName {
  display: flex;
  align-items: center;
  justify-content: center;
}
.AppMenuDesktop .boardName {
  font-weight: normal;
}
.AppMenuDesktop ul.menuItems {
  flex-grow: 1;
  background: #007DE0;
  display: flex;
  align-items: stretch;
}
.AppMenuDesktop ul.menuItems > li {
  flex-grow: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}
.AppMenuDesktop ul.menuItems > li > a {
  color: white;
  text-decoration: none;
}
.AppMenuDesktop .technicianSettings {
  background: var(--red-400);
}
</style>